import type {
  AppointmentForm,
  FormatterFn,
} from '@devexpress/dx-react-scheduler';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import type { ByWeekday, Options } from 'rrule';
import { Weekday } from 'rrule';

import {
  changeRecurrenceOptions,
  getDaysOfWeekArray,
  getDaysOfWeekDates,
  getRecurrenceOptions,
  handleWeekDaysChange,
} from '@utils/routines';

/** Properties passed to a component that renders a weekly recurrence selector on the appointment form. */
interface WeeklyRecurrenceSelectorProps {
  /** A function that formats dates based on the locale. */
  formatDate: FormatterFn;
  /** A number between 0 (Sunday) and 6 (Saturday) that specifies the first day of the week. */
  firstDayOfWeek: number;
  /** Specifies the recurrence rule. */
  rRule: string;
  /** Specifies whether the weekly recurrence selector is read-only. */
  readOnly: boolean;
  /** Handles appointment field value changes. */
  onValueChange: (nextFieldValue: Record<string, any>) => void;
}

const isCurrentWeekDay = (byweekday: ByWeekday[], currentWeekDay: number) =>
  byweekday.findIndex((weekday) => {
    if (weekday instanceof Weekday) {
      return weekday.weekday === currentWeekDay;
    } else if (typeof weekday === 'number') {
      return weekday === currentWeekDay;
    }
    return false;
  }) > -1;

const WeeklyRecurrenceSelector = ({
  ...props
}: AppointmentForm.WeeklyRecurrenceSelectorProps) => {
  const { rRule, firstDayOfWeek } = props;
  const newProps = props as unknown as WeeklyRecurrenceSelectorProps;
  const { onValueChange } = newProps;
  const [buttons, setButtons] = useState<{ name: string; value: number }[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [recurrenceOptions, setRecurrenceOptions] = useState<Partial<Options>>(
    {}
  );

  useEffect(() => {
    const options = getRecurrenceOptions(rRule);
    const { byweekday } = options;
    const daysOfWeekArray = getDaysOfWeekArray(firstDayOfWeek);
    const daysOfWeekDates = getDaysOfWeekDates(firstDayOfWeek);
    const items = daysOfWeekArray.map((day, index) => ({
      name: dayjs(daysOfWeekDates[index]).format('ddd'),
      value: day,
    }));
    setRecurrenceOptions(getRecurrenceOptions(rRule));
    setButtons(items);
    setSelected(
      byweekday instanceof Array
        ? items
            .filter(({ value }) => {
              const yes = isCurrentWeekDay(byweekday, value);
              return yes;
            })
            .map(({ value }) => value)
        : []
    );
  }, [firstDayOfWeek, rRule]);

  return (
    <ToggleButtonGroup sx={{ flexWrap: 'wrap', mb: 2 }} value={selected}>
      {buttons.map(({ name, value }, index) => {
        return (
          <ToggleButton
            key={`mui-button-${index}`}
            onClick={() => {
              const newRule = changeRecurrenceOptions(
                handleWeekDaysChange(recurrenceOptions, value)
              );
              onValueChange({
                rRule: newRule,
              });
            }}
            value={value}
          >
            {name}
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
};

export default WeeklyRecurrenceSelector;
